import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { Header } from "@/components/wp/Header";
import { Footer } from "@/components/wp/Footer";
import { BottomNav } from "@/components/wp/BottomNav";
import { CartDrawer } from "@/components/wp/CartDrawer";
import { ConciergeWidget } from "@/components/wp/ConciergeWidget";
import { CursorSparkle } from "@/components/wp/CursorSparkle";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ParcelDetail from "@/pages/ParcelDetail";
import ProductDetail from "@/pages/ProductDetail";
import Builder from "@/pages/Builder";
import Reminders from "@/pages/Reminders";
import Checkout from "@/pages/Checkout";
import Founder from "@/pages/Founder";
import Search from "@/pages/Search";
import MakeItPersonal from "@/pages/MakeItPersonal";
import GiftPage from "@/pages/GiftPage";
import OurStory from "@/pages/OurStory";
import TrackOrder from "@/pages/TrackOrder";
import Contact from "@/pages/Contact";
import CorporateGifting from "@/pages/CorporateGifting";
import CustomSilver from "@/pages/CustomSilver";
import AdminApp from "@/admin/AdminApp";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" }); }, [pathname]);
  return null;
}

function MediaProtection() {
  // Best-effort: block context menu / drag on product imagery site-wide.
  useEffect(() => {
    const onCtx = (e) => { if (e.target && e.target.tagName === "IMG") e.preventDefault(); };
    const onDrag = (e) => { if (e.target && e.target.tagName === "IMG") e.preventDefault(); };
    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("dragstart", onDrag);
    return () => { document.removeEventListener("contextmenu", onCtx); document.removeEventListener("dragstart", onDrag); };
  }, []);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
        className="min-h-[60vh] pb-28 md:pb-0"
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/search" element={<Search />} />
          <Route path="/founder" element={<Founder />} />
          <Route path="/personalize" element={<MakeItPersonal />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/track" element={<TrackOrder />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/corporate-gifting" element={<CorporateGifting />} />
          <Route path="/custom-silver" element={<CustomSilver />} />
          <Route path="/parcel/:slug" element={<ParcelDetail />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/build" element={<Builder />} />
          {/* AI Studio is fully hidden from customers; keep any legacy links safe */}
          <Route path="/ai" element={<Navigate to="/shop" replace />} />
          <Route path="/reminders" element={<Reminders />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </motion.main>
    </AnimatePresence>
  );
}

function StoreLayout() {
  return (
    <>
      <CursorSparkle />
      <MediaProtection />
      <ScrollToTop />
      <Header />
      <AnimatedRoutes />
      <Footer />
      <BottomNav />
      <CartDrawer />
      <ConciergeWidget />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
          <CartProvider>
            <Routes>
              {/* Private, standalone gift page (no store chrome) */}
              <Route path="/gift/:token" element={<><ScrollToTop /><GiftPage /></>} />
              {/* Admin CMS (standalone layout) */}
              <Route path="/admin/*" element={<AdminApp />} />
              {/* Full storefront layout for everything else */}
              <Route path="/*" element={<StoreLayout />} />
            </Routes>
            <Toaster position="top-center" closeButton />
          </CartProvider>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
